import {
    type ProductData,
    type RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import { type TrackingContext } from "@/lib/utils/tracking/trackingDataWithOrigin"
import {
    type CheckoutAPIProduct,
    type MinimizedCentraProduct,
    type RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import { getEnglishProductName } from "./getEnglishProductName"

export interface ItemContext {
    promotion_id?: string
    promotion_name?: string
    item_list_name?: string
    item_list_id?: string
    index?: number
    item_size?: string
    quantity?: number
}

export interface TransformedItemData {
    item_id: string
    item_name: string
    item_brand: string
    item_color?: string
    item_size?: string
    item_category?: string
    item_category2?: string
    item_category3?: string
    item_category4?: string
    item_category5?: string
    item_variant?: string
    item_stock_status: "In stock" | "Out of stock"
    price: number
    quantity: number
    promotion_id?: string
    promotion_name?: string
    item_list_name?: string
    item_list_id?: string
    index?: number
}

export type NormalizedTrackingProduct = {
    item_id: string
    item_name: string
    item_category?: string | null
    item_category2?: string | null
    item_category3?: string | null
    item_category4?: string | null
    item_variant?: string
    item_size?: string
    item_stock_status: "In stock" | "Out of stock"
    price?: number
    quantity?: number
    promotion_id?: string
    promotion_name?: string
    item_list_name?: string
    item_list_id?: string
    index?: number
    [key: string]: any
}

function isMinimizedCentraProduct(product: any): product is MinimizedCentraProduct {
    return (
        product &&
        typeof product === "object" &&
        "centraProduct" in product &&
        "name" in product
    )
}

function isCheckoutAPIProduct(product: any): product is CheckoutAPIProduct {
    return (
        product && typeof product === "object" && "sku" in product && "brand" in product
    )
}

function isRelatedProduct(product: any): product is RelatedProduct {
    return (
        product && typeof product === "object" && "sku" in product && "related" in product
    )
}

function isProductData(product: any): product is ProductData {
    return (
        product && typeof product === "object" && "product" in product && "sku" in product
    )
}

function isRelatedVariant(product: any): product is RelatedVariant {
    return (
        product &&
        typeof product === "object" &&
        "relation" in product &&
        "name" in product &&
        product.relation === "variant"
    )
}

function getStockStatus(
    product: any,
    selectedSize?: string
): "In stock" | "Out of stock" {
    // If a specific size is selected, check that size variant's stock status
    if (selectedSize && product.items && Array.isArray(product.items)) {
        // For cart items, the size might be stored differently than product.items[].name
        // Try to find the item by name first, then by other size-related fields
        let selectedItem = product.items.find((item: any) => item.name === selectedSize)

        // If not found by name, try to find by other size fields
        if (!selectedItem) {
            selectedItem = product.items.find(
                (item: any) =>
                    item.size === selectedSize ||
                    item.sizeId === selectedSize ||
                    item.sku?.includes(selectedSize)
            )
        }

        if (selectedItem) {
            // Since the items don't have a 'stock' field, if we found the item in the array,
            // it means it's available (otherwise it wouldn't be in the items array)
            // Use the product's overall availability or assume in stock if item exists
            return product.available === false ? "Out of stock" : "In stock"
        }
    }

    // Fallback to overall product availability
    if (typeof product.available === "boolean") {
        return product.available ? "In stock" : "Out of stock"
    }
    if (typeof product.stockStatus === "string") {
        return product.stockStatus === "In stock" ? "In stock" : "Out of stock"
    }
    // Default to "Out of stock" if we can't determine
    return "Out of stock"
}

function getCategoryFromUri(categoryUri: string): {
    item_category?: string
    item_category2?: string
    item_category3?: string
    item_category4?: string
} {
    // Split the URI by '/' and filter out empty strings
    const uriParts = categoryUri.split("/").filter(Boolean)

    return {
        item_category: uriParts[0] || undefined,
        item_category2: uriParts[1] || undefined,
        item_category3: uriParts[2] || undefined,
        item_category4: uriParts[3] || undefined
    }
}

function getCategoryHierarchy(product: any): {
    item_category?: string
    item_category2?: string
    item_category3?: string
    item_category4?: string
    item_category5?: string
} {
    // If product has categoryUri, use it as the truthful source
    if (product.categoryUri && typeof product.categoryUri === "string") {
        return getCategoryFromUri(product.categoryUri)
    }

    return {
        item_category: undefined,
        item_category2: undefined,
        item_category3: undefined,
        item_category4: undefined
    }
}

function extractCurrencyFromProduct(product: any): string | undefined {
    // Extract currency from the formatted price string, e.g., "123.00 EUR" -> "EUR"
    if (typeof product.price === "string" && product.price.length >= 3) {
        return product.price.slice(-3)
    }

    // Fallback to currency field if available
    if (typeof product.currency === "string") {
        return product.currency
    }

    return undefined
}

export function adaptProductForTracking(
    product: any,
    opts?: TrackingContext
): NormalizedTrackingProduct {
    // If forceInStock is true, override stock status
    const stockStatus = opts?.forceInStock
        ? "In stock"
        : getStockStatus(product, opts?.item_size)
    const categoryHierarchy = getCategoryHierarchy(product)
    const currency = extractCurrencyFromProduct(product)

    const currentFullProductId = (Array.isArray(product.items) ? product.items : []).find(
        (item: any) => item.name === opts?.item_size
    )?.sku

    const firstFullProductIdInItems =
        Array.isArray(product.items) && product.items.length > 0
            ? product.items?.[0]?.sku
            : undefined

    const itemPrice =
        opts?.final_price_all_discounts != null && opts.total_quantity
            ? opts.final_price_all_discounts / opts.total_quantity
            : (product.priceAsNumber ?? product.priceBeforeDiscountAsNumber)

    const itemDiscount =
        opts?.base_price_with_tax && opts?.final_price_with_tax && opts.total_quantity
            ? (opts.base_price_with_tax - opts.final_price_with_tax) / opts.total_quantity
            : product.priceBeforeDiscountAsNumber > product.priceAsNumber
              ? product.priceBeforeDiscountAsNumber - product.priceAsNumber
              : 0

    const getItemIdFromProductSku = () => {
        if (firstFullProductIdInItems) return firstFullProductIdInItems

        if (
            currentFullProductId ||
            firstFullProductIdInItems ||
            typeof (product as any).productSku === "string"
        ) {
            return (product as any).productSku
        }

        return "unknown"
    }

    const getItemIdFromSku = () => {
        if (firstFullProductIdInItems) return firstFullProductIdInItems

        const fallbackId =
            currentFullProductId ||
            firstFullProductIdInItems ||
            (typeof product.sku === "string" ? product.sku : "unknown")

        return fallbackId
    }

    if (isCheckoutAPIProduct(product)) {
        return {
            item_id:
                firstFullProductIdInItems ||
                currentFullProductId ||
                product.productSku ||
                "unknown",
            // Old, keep for now if they want to have this intact and add a new key+value instead
            // item_id: product.productSku || "unknown",
            item_name: getEnglishProductName(product),
            ...categoryHierarchy,
            item_variant: product.centraVariant || undefined,
            item_size: product.items?.[0]?.sku?.slice(-4) || undefined,
            item_stock_status: stockStatus,
            price: parseFloat(itemPrice.toFixed(1)),
            discount: parseFloat(itemDiscount.toFixed(1)),
            quantity: opts?.quantity || 1,
            promotion_id: opts?.promotion_id,
            promotion_name: opts?.promotion_name,
            item_list_name: opts?.item_list_name,
            item_list_id: opts?.item_list_id,
            index: opts?.index,
            currency
        }
    }

    if (isMinimizedCentraProduct(product)) {
        return {
            item_id: (() => {
                if (firstFullProductIdInItems) return firstFullProductIdInItems
                const items = product.items as unknown
                // If items is a string
                if (typeof items === "string") {
                    if (items.includes("-")) {
                        const fullItemId = items.split("-")?.[1]
                        return fullItemId
                    }
                    // Old, keep for now if they want to have this intact and add a new key+value instead
                    // if (items.includes("-")) {
                    //     // Use left side of dash
                    //     return items.slice(0, items.indexOf("-"))
                    // } else {
                    //     // Remove last 8 characters
                    //     return items.length > 8 ? items.slice(0, -8) : items
                    // }
                }
                // If items is an array
                if (Array.isArray(items) && items.length > 0) {
                    const first = items[0]
                    const sku =
                        typeof first === "object" && first !== null ? first.sku : first

                    if (typeof sku === "string") {
                        const hasDash = sku.includes("-")
                        return hasDash ? sku.split("-")?.[1] : sku

                        // Old, keep for now if they want to have this intact and add a new key+value instead
                        // if (sku.includes("-")) {
                        //     return sku.slice(0, sku.indexOf("-"))
                        // } else {
                        //     return sku.length > 8 ? sku.slice(0, -8) : sku
                        // }
                    }
                }
                // Fallback to centraProduct
                if (typeof product.centraProduct === "string") {
                    return product.centraProduct
                    // Old, keep for now if they want to have this intact and add a new key+value instead
                    // return product.centraProduct.length > 8
                    //     ? product.centraProduct.slice(0, -8)
                    //     : product.centraProduct
                }
                return "unknown"
            })(),
            item_name: getEnglishProductName(product),
            ...categoryHierarchy,
            item_variant: (() => {
                const items = product.items as unknown as any
                const fullSku = items?.[0]?.sku || items
                if (typeof fullSku === "string") {
                    const dashIndex = fullSku.indexOf("-")
                    if (dashIndex !== -1) {
                        const leftSide = fullSku.slice(0, dashIndex)
                        let rightSide = fullSku.slice(dashIndex + 1)
                        // Remove the leftSide from the start of rightSide if present
                        if (rightSide.startsWith(leftSide)) {
                            rightSide = rightSide.slice(leftSide.length)
                        }
                        // Remove last 4 (size) to get only variant
                        if (rightSide.length > 4) {
                            return rightSide.slice(0, -4)
                        }
                    }
                }
                return undefined
            })(),
            item_size: product.items?.slice(-4) || undefined,
            item_stock_status: product.available ? "In stock" : "Out of stock",
            price: parseFloat(itemPrice.toFixed(1)) || undefined,
            discount: parseFloat(itemDiscount.toFixed(1)),
            quantity: opts?.quantity || 1,
            promotion_id: opts?.promotion_id,
            promotion_name: opts?.promotion_name,
            item_list_name: opts?.item_list_name,
            item_list_id: opts?.item_list_id,
            index: opts?.index,
            currency
        }
    }
    if (isRelatedProduct(product)) {
        return {
            item_id: getItemIdFromProductSku(),
            // Old, keep for now if they want to have this intact and add a new key+value instead
            // item_id:
            //     typeof (product as any).productSku === "string"
            //         ? (product as any).productSku
            //         : "unknown",
            item_name: getEnglishProductName(product),
            ...categoryHierarchy,
            item_variant:
                typeof (product as any).sku === "string"
                    ? (product as any).sku.slice(-4)
                    : undefined,
            item_size: opts?.item_size,
            item_stock_status: stockStatus,
            price: parseFloat(itemPrice.toFixed(1)) || undefined,
            quantity: opts?.quantity || 1,
            promotion_id: opts?.promotion_id,
            promotion_name: opts?.promotion_name,
            item_list_name: opts?.item_list_name,
            item_list_id: opts?.item_list_id,
            index: opts?.index,
            currency
        }
    }

    if (isProductData(product)) {
        return {
            item_id: getItemIdFromProductSku(),
            // Old, keep for now if they want to have this intact and add a new key+value instead
            // item_id: typeof product.productSku === "string" ? product.productSku : "unknown",
            item_name: getEnglishProductName(product),
            ...categoryHierarchy,
            item_variant: product.sku?.slice(-4) || undefined,
            item_size: opts?.item_size,
            item_stock_status: stockStatus,
            price: parseFloat(itemPrice.toFixed(1)),
            discount: parseFloat(itemDiscount.toFixed(1)),
            quantity: opts?.quantity || 1,
            promotion_id: opts?.promotion_id,
            promotion_name: opts?.promotion_name,
            item_list_name: opts?.item_list_name,
            item_list_id: opts?.item_list_id,
            index: opts?.index,
            currency
        }
    }
    if (isRelatedVariant(product)) {
        return {
            item_id: getItemIdFromSku(),
            // Old, keep for now if they want to have this intact and add a new key+value instead
            // item_id: typeof product.sku === "string" ? product.sku : "unknown",
            item_name: getEnglishProductName(product),
            item_variant: product.sku?.slice(-4) || undefined,
            item_size: opts?.item_size,
            item_stock_status: product.available ? "In stock" : "Out of stock",
            price: parseFloat(itemPrice.toFixed(1)),
            discount: parseFloat(itemDiscount.toFixed(1)),
            quantity: opts?.quantity || 1,
            promotion_id: opts?.promotion_id,
            promotion_name: opts?.promotion_name,
            item_list_name: opts?.item_list_name,
            item_list_id: opts?.item_list_id,
            index: opts?.index,
            currency
        }
    }

    return {
        item_id: getItemIdFromSku(),
        // Old, keep for now if they want to have this intact and add a new key+value instead
        // item_id: product.sku.length > 11 ? product.sku.slice(0, 7) : product.sku,
        item_name: getEnglishProductName(product),
        item_variant: product.sku?.slice(7, 11) || undefined,
        item_stock_status: stockStatus,
        quantity: opts?.quantity || 1,
        promotion_id: opts?.promotion_id,
        promotion_name: opts?.promotion_name,
        item_list_name: opts?.item_list_name,
        item_list_id: opts?.item_list_id,
        index: opts?.index,
        currency
    }
}
